import useTranslation from 'next-translate/useTranslation'
import WarningIcon from '@ancon/wildcat-ui/shared/icons/warning-outline.svg'
import noop from 'lodash/noop'

import BodyText from '../../../app/components/BodyText'
import Button from '../../../app/components/Button'
import HeadingText from '../../../app/components/HeadingText'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import Modal from '../../../app/components/modal/Modal'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import {
  checkoutDeletePendingSelector,
  checkoutSystemProductWarningSelector,
} from '../../store/checkoutSelectors'
import { setProductDetailsModalVisible } from '../../../product/store/productSlice'
import { setWalletTopUpModalVisible } from '../../../wallets/store/walletsSlice'
import { deleteCheckout } from '../../store/checkoutThunks'
import { checkoutSetSystemProductWarningModalVisible } from '../../store/checkoutSlice'
import { CheckoutSystemProductWarningType } from '../../types'
import useNavigateGiftCardPurchase from '../../../giftCards/hooks/useNavigateGiftCardPurchase'

import styles from './CheckoutSystemProductWarningModal.module.scss'

interface CheckoutSystemProductWarningModalProps {
  onProceed?: () => void
}

function CheckoutSystemProductWarningModal({
  onProceed,
}: CheckoutSystemProductWarningModalProps) {
  const { t } = useTranslation('common')

  const dispatch = useAppDispatch()

  const navigateGiftCardPurchase = useNavigateGiftCardPurchase()

  const isCheckoutDeletePending = useAppSelector(checkoutDeletePendingSelector)

  const warning = useAppSelector(checkoutSystemProductWarningSelector)

  function hideModal() {
    dispatch(
      checkoutSetSystemProductWarningModalVisible(
        CheckoutSystemProductWarningType.None,
      ),
    )
  }

  function handleCloseModal() {
    if (!isCheckoutDeletePending) {
      hideModal()
    }
  }

  function handleDeleteCheckoutSuccess() {
    switch (warning) {
      case CheckoutSystemProductWarningType.AddWalletTopUp:
        dispatch(setWalletTopUpModalVisible(true))
        break

      case CheckoutSystemProductWarningType.AddGiftCard:
        onProceed?.()
        break

      case CheckoutSystemProductWarningType.AddProductToWalletCheckout:
      case CheckoutSystemProductWarningType.AddProductToGiftCardCheckout:
        dispatch(setProductDetailsModalVisible(true))
        break

      case CheckoutSystemProductWarningType.StartGiftCardPurchase:
        navigateGiftCardPurchase()
        break

      default:
        break
    }

    hideModal()
  }

  function handleStartNewOrder() {
    dispatch(deleteCheckout())
      .unwrap()
      .then(handleDeleteCheckoutSuccess)
      .catch(noop)
  }

  function getWarningMessage() {
    if (warning) {
      return t(`checkout.systemProductWarningModal.message.${warning}`)
    }

    return null
  }

  const message = getWarningMessage()

  return (
    <Modal
      isOpen={!!warning}
      onClose={handleCloseModal}
      mobileModalMode="bottom-sheet"
      contentClassName={styles.modalContent}
      hideClose
    >
      <div className={styles.modalHeader}>
        <WarningIcon />
        <HeadingText as="h2">
          {t('checkout.systemProductWarningModal.title')}
        </HeadingText>
      </div>
      {message && <BodyText color="body-1">{message}</BodyText>}
      <div className={styles.modalFooter}>
        <Button
          variant="secondary"
          size="large"
          onClick={handleCloseModal}
          disabled={isCheckoutDeletePending}
        >
          {t('checkout.systemProductWarningModal.keepExistingOrder')}
        </Button>
        <Button
          size="large"
          loading={isCheckoutDeletePending}
          onClick={handleStartNewOrder}
        >
          {t('checkout.systemProductWarningModal.startNewOrder')}
        </Button>
      </div>
    </Modal>
  )
}

export default CheckoutSystemProductWarningModal
